/**
 * Component to handle table data cell that can be copied to clipboard.
 */
import React, { useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";

/**
 * @param {mixed} param0.value - Value to be copied - and default display if no children are present for render
 * @param {number} param0.copiedHideAfter - Time in milliseconds to hide the copied message
 * @param {object} param0.rest - Other props to be passed to the component
 * @param {string} param0.rest.children - Children
 * @returns {JSX.Element}
 * @component
 */
const TableCellCopy = ({ value, copiedHideAfter = 2000, ...rest }) => {
  const [copied, setCopied] = useState(false);

  const copyEmail = (e) => {
    e.stopPropagation(); // Prevent row selection/expansion if implemented
    if (!value || !navigator.clipboard?.writeText) {
      return;
    }

    navigator.clipboard.writeText(value).then(
      () => {
        setCopied(true);
        // Reset copied state after 2 seconds
        setTimeout(() => setCopied(false), copiedHideAfter);
      },
      () => setCopied(false),
    );
  };

  return (
    <Tooltip
      title={copied ? "Copied!" : `Click to copy: ${value}`}
      placement="top"
      open={copied ? true : undefined}
    >
      <Box
        onClick={copyEmail}
        role="button"
        tabIndex={value ? 0 : -1}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            copyEmail(event);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          maxWidth: "100%",
          cursor: value ? "pointer" : "default",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          color: copied ? "primary.main" : "inherit",
          transition: "color 0.2s",
        }}
      >
        <ContentCopyIcon
          fontSize="small"
          sx={{
            mr: 1,
            flexShrink: 0,
            fontSize: "0.9rem",
            verticalAlign: "middle",
            opacity: 0.6,
            color: copied ? "primary.main" : "inherit",
          }}
        />
        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {rest?.children || value}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default TableCellCopy;
